/*
 * Cart subscription box (snippets/subscription-cart-box.liquid).
 * Puts every subscribable cart line on the chosen Appstle selling plan
 * (every 30 / 60 / 90 days) through /cart/change.js, optionally raising the
 * quantity so one delivery lasts until the next one, then re-renders the
 * drawer through window.SeedCartDrawer (assets/seed-cart.js). On /cart the
 * page reloads instead. "Turn off" removes the selling plan again.
 * The rhythm whose deliveries last closest to it is marked „Rekomenduojame“
 * and preselected (unless the shopper picked one). Checkout waits while the
 * subscription lines are below the minimum: the box then carries
 * [data-sub-short] and the checkout submit is stopped here.
 */
(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const OPEN_KEY = 'grasoleSubBoxOpen';
  const RHYTHM_KEY = 'grasoleSubRhythm';
  const PREFERRED = [60, 90, 30];
  const isEn = (document.documentElement.lang || '').toLowerCase().startsWith('en');
  const money = (cents) => {
    try {
      return new Intl.NumberFormat(isEn ? 'en-IE' : 'lt-LT', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    } catch (_) {
      return `€${(cents / 100).toFixed(2)}`;
    }
  };
  // Units needed so one delivery lasts until the next one (15 % tolerance).
  const suggest = (days, rhythm, qty) => {
    if (!days || !rhythm) return qty;
    return Math.max(qty, Math.ceil(rhythm / days - 0.15));
  };

  const readBox = (box) => {
    const checked = box.querySelector('input[name="sub_rhythm"]:checked');
    const items = [...box.querySelectorAll('[data-sub-item]')].map((el) => ({
      el,
      variant: Number(el.dataset.variant),
      qty: Number(el.dataset.qty) || 1,
      days: Number(el.dataset.days) || 0,
      unit: Number(el.dataset.unit) || 0,
    }));
    return {
      rhythm: checked ? Number(checked.value) : 0,
      planId: checked ? Number(checked.dataset.planId) : 0,
      adjust: Boolean(box.querySelector('[data-sub-adjust]')?.checked),
      items,
    };
  };

  const preview = (box) => {
    const { rhythm, adjust, items } = readBox(box);
    if (!rhythm) return;
    let total = 0;
    items.forEach((item) => {
      const note = item.el.querySelector('[data-sub-note]');
      const target = adjust ? suggest(item.days, rhythm, item.qty) : item.qty;
      total += target * item.unit;
      if (!note || !item.days) return;
      const lasts = target * item.days;
      if (target > item.qty) {
        note.textContent = isEn
          ? `${item.qty} → ${target} pcs · ~${lasts} d.`
          : `${item.qty} → ${target} vnt. · ~${lasts} d.`;
        note.classList.add('is-up');
      } else {
        note.textContent = isEn
          ? `${target} pcs · ~${lasts} d.`
          : `${target} vnt. · ~${lasts} d.`;
        note.classList.remove('is-up');
        note.classList.toggle('is-short', lasts < rhythm * 0.85);
      }
    });
    const per = box.querySelector('[data-sub-per]');
    if (per && total > 0) {
      per.textContent = isEn
        ? `Each delivery: ${money(total)}`
        : `Viena siunta: ${money(total)}`;
      per.hidden = false;
    }
  };

  // Rhythm whose deliveries last closest to it for the quantities in the cart (never raised, 2026-11-04).
  // Ties go to the usual 60 days, then to the longer rhythm (fewer parcels).
  const recommendRhythm = (box) => {
    const known = readBox(box).items.filter((item) => item.days > 0);
    const radios = [...box.querySelectorAll('input[name="sub_rhythm"]')];
    if (!known.length || !radios.length) return null;
    const rank = (days) => (PREFERRED.indexOf(days) === -1 ? 9 : PREFERRED.indexOf(days));
    return radios
      .map((radio) => {
        const rhythm = Number(radio.value);
        const score = known.reduce((sum, item) => sum + Math.abs((item.qty * item.days) / rhythm - 1), 0) / known.length;
        return { radio, rhythm, score: Math.round(score * 1000) };
      })
      .sort((a, b) => a.score - b.score || rank(a.rhythm) - rank(b.rhythm))[0];
  };

  const markRecommended = (box) => {
    const rec = recommendRhythm(box);
    box.querySelectorAll('.sub-chip').forEach((chip) => chip.classList.toggle('is-rec', Boolean(rec) && chip.contains(rec.radio)));
    if (!rec || box.classList.contains('sub-box--on')) return;
    let picked = 0;
    try { picked = Number(sessionStorage.getItem(RHYTHM_KEY)) || 0; } catch (_) {}
    const chosen = picked && box.querySelector(`input[name="sub_rhythm"][value="${picked}"]`);
    (chosen || rec.radio).checked = true;
  };

  const previewAll = () => {
    document.querySelectorAll('[data-sub-box]').forEach((box) => {
      try {
        if (sessionStorage.getItem(OPEN_KEY) === '1' || box.querySelector('[data-sub-short]')) box.open = true;
      } catch (_) {}
      markRecommended(box);
      preview(box);
    });
  };

  const postChange = async (payload) => {
    const response = await fetch(`${ROOT}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    let json = null;
    try { json = await response.json(); } catch (_) {}
    if (!response.ok || !json) {
      throw new Error((json && (json.description || json.message)) || `cart/change ${response.status}`);
    }
    return json;
  };

  const showError = (box, message) => {
    const error = box.querySelector('[data-sub-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  };

  const apply = async (box, turnOff) => {
    const { planId, rhythm, adjust, items } = readBox(box);
    if (!turnOff && !planId) return;
    const targets = new Map();
    items.forEach((item) => {
      targets.set(item.variant, turnOff || !adjust ? item.qty : suggest(item.days, rhythm, item.qty));
    });
    const plan = turnOff ? null : planId;
    const inDrawer = Boolean(box.closest('[data-cart-drawer]'));
    const drawer = inDrawer ? window.SeedCartDrawer : null;
    if (drawer && !drawer.beginMutation()) return;
    box.setAttribute('aria-busy', 'true');
    showError(box, '');
    try {
      const cartResponse = await fetch(`${ROOT}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      let cart = await cartResponse.json();
      // "Buy once" takes every subscription line off its plan, whatever the box lists.
      if (turnOff) {
        targets.clear();
        cart.items.forEach((line) => {
          if (line.selling_plan_allocation) targets.set(line.variant_id, line.quantity);
        });
      }
      const done = new Set();
      // Line keys change after each update, so the next line is looked up in the fresh cart every time.
      for (let guard = 0; guard < 60; guard += 1) {
        const next = cart.items.find((line) => {
          if (!targets.has(line.variant_id) || done.has(line.variant_id)) return false;
          if (turnOff && !line.selling_plan_allocation) return false;
          const current = (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan.id) || null;
          return current !== plan || line.quantity !== targets.get(line.variant_id);
        });
        if (!next) break;
        cart = await postChange({ id: next.key, quantity: targets.get(next.variant_id), selling_plan: plan });
        done.add(next.variant_id);
      }
      try { sessionStorage.setItem(OPEN_KEY, '1'); } catch (_) {}
      if (drawer) {
        if (!(await drawer.refreshWithRetry())) window.location.assign(`${ROOT}cart`);
      } else {
        window.location.reload();
      }
    } catch (error) {
      try { console.warn('[subscription-cart]', error); } catch (_) {}
      showError(box, (error && error.message) || (isEn ? 'Something went wrong. Please try again.' : 'Nepavyko. Pabandykite dar kartą.'));
      if (drawer) await drawer.refreshWithRetry();
    } finally {
      box.removeAttribute('aria-busy');
      if (drawer) drawer.endMutation();
    }
  };

  document.addEventListener('change', (event) => {
    const box = event.target.closest && event.target.closest('[data-sub-box]');
    if (!box) return;
    if (event.target.name === 'sub_rhythm') {
      try { sessionStorage.setItem(RHYTHM_KEY, event.target.value); } catch (_) {}
    }
    if (event.target.name === 'sub_rhythm' || event.target.matches('[data-sub-adjust]')) preview(box);
  });

  // No checkout while the subscription lines are below the minimum (box flag [data-sub-short]).
  // Window capture runs before the drawer's own submit handling.
  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    const isCheckout = (submitter && submitter.name === 'checkout') || (!submitter && form.querySelector('[name="checkout"]'));
    if (!isCheckout) return;
    const flag = document.querySelector('[data-sub-box] [data-sub-short]');
    if (!flag) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const box = flag.closest('[data-sub-box]');
    box.open = true;
    const warn = box.querySelector('.sub-warn');
    if (warn) {
      warn.classList.remove('is-flash');
      void warn.offsetWidth;
      warn.classList.add('is-flash');
    }
    box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, true);

  document.addEventListener('toggle', (event) => {
    const box = event.target;
    if (!(box instanceof HTMLElement) || !box.matches('[data-sub-box]')) return;
    try { sessionStorage.setItem(OPEN_KEY, box.open ? '1' : '0'); } catch (_) {}
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-sub-apply], [data-sub-off]');
    if (!button) return;
    const box = button.closest('[data-sub-box]');
    if (!box) return;
    event.preventDefault();
    apply(box, button.hasAttribute('data-sub-off'));
  });

  document.addEventListener('cart:updated', () => window.requestAnimationFrame(previewAll));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', previewAll);
  else previewAll();
})();
